import { createRouter, type RouterHistory } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { crmPublicRoutes, crmRoutes } from '../modules/crm/routes';
import { financialsRoutes } from '../modules/financials/routes';
import { integrationsRoutes } from '../modules/integrations/routes';
import { platformRoutes } from '../modules/platform/routes';
import { portalAdminRoutes, portalRoutes } from '../modules/portal/routes';
import { projectsRoutes } from '../modules/projects/routes';
import { companySignInRoutes } from '../modules/workspace/auth/branded-routes';
import { workspaceAppRoutes, workspacePublicRoutes } from '../modules/workspace/routes';
import { appLayoutRoute, portalLayoutRoute, publicLayoutRoute, rootRoute } from './parents';
import { LoadingState } from '@buildbee/ui';

/**
 * Root route tree. Module route arrays are pre-registered here; module agents only edit
 * `src/modules/<module>/routes.tsx`, never this file.
 */
export const routeTree = rootRoute.addChildren([
  publicLayoutRoute.addChildren([...workspacePublicRoutes, ...companySignInRoutes, ...crmPublicRoutes]),
  appLayoutRoute.addChildren([
    ...workspaceAppRoutes,
    ...crmRoutes,
    ...projectsRoutes,
    ...financialsRoutes,
    ...integrationsRoutes,
    ...portalAdminRoutes,
  ]),
  portalLayoutRoute.addChildren([...portalRoutes]),
  ...platformRoutes,
]);

/** `history` defaults to the browser history; tests pass a memory history. */
export function createAppRouter(queryClient: QueryClient, options: { history?: RouterHistory } = {}) {
  return createRouter({
    routeTree,
    ...(options.history ? { history: options.history } : {}),
    context: { queryClient },
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    defaultPendingComponent: () => <LoadingState />,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
